/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  CELL_TEXT_RIGHT_CLASS,
  createDefaultGridModel,
  defaultCurrencyValueFormatterDecorator,
  EDITABLE_CELL_YELLOW_CLASS,
  editableValueFormatterDecorator,
  identityCellRenderer,
  likeEditableCellRenderer,
  noneCellRenderer,
  pinnedRowPostCreateDecorator,
  ROW_DIRTY_CLASS,
  textOverlayRowPostCreate,
} from '@gh/common-internal-shared';
import { IS_REGION_AU, NumberTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableNumberColumn,
  buildNumberColumn,
  cellRendererSelectorForPinnedRow,
  emptyValueFormatter,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  mergePinnedRowValueFormatter,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import { toBoolean } from '@gh/core-util';
import { Observable } from 'rxjs';
import {
  calculateWipJobTotal,
  getWipJobId,
  validateWipJobField,
  WipJob,
  wipJobFieldState,
} from '../../resources/wip.model';

@Component({
  standalone: false,
  selector: 'gh-wip-job-list',
  templateUrl: 'wip-job-list.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WipJobListComponent implements OnInit {
  @Input()
  focusedCell: FocusedCell;

  @Input()
  editable$: Observable<boolean>;

  @Output()
  setField = new EventEmitter<{ id: ID; value?: number }>();

  @Output()
  focusedCellChange = new EventEmitter<Maybe<FocusedCell>>();

  _jobs: WipJob[];
  totals: WipJob[];

  gridDefinition: GridDefinition;

  @Input()
  set jobs(jobs: WipJob[]) {
    this._jobs = jobs;
    this.totals = [calculateWipJobTotal(jobs)];
  }

  get jobs(): WipJob[] {
    return this._jobs;
  }

  constructor(private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    const model = createDefaultGridModel({
      editable: this.editable$,
      getId: getWipJobId,
      getCellDef: wipJobFieldState,
      validate: validateWipJobField,
      command: (job, _, value) => this.setField.emit({ id: getWipJobId(job), value }),
    });
    this.gridDefinition = this.gridDefinitionFactory.buildManualWithModel({
      model,
      options: {
        defaultColDef: {
          sortable: false,
        },
        processRowPostCreate: pinnedRowPostCreateDecorator(textOverlayRowPostCreate(`${this.translations.get(
          'lbl_totals')}:`)),
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: WipJob }) => toBoolean(data.isDirty),
        },
      },
      columns: <any[]>[
        mergePinnedRowValueFormatter({
          field: 'jobNumber',
          headerName: this.translations.get('lbl_job_number'),
          width: 120,
        }, emptyValueFormatter),
        mergePinnedRowValueFormatter({
          field: 'customerName',
          headerName: this.translations.get('lbl_contact_customer'),
          width: 200,
        }, emptyValueFormatter),
        mergePinnedRowValueFormatter({
          field: 'totalPrice',
          headerName: this.translations.get('lbl_total_price_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        mergePinnedRowValueFormatter({
          field: 'drawnToDate',
          headerName: this.translations.get('lbl_drawn_to_date_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        {
          field: 'drawnToDatePct',
          headerName: this.translations.get('lbl_drawn_to_date_pct'),
          width: 100,
          ...buildNumberColumn(NumberTypes.Percents_2),
          cellRenderer: likeEditableCellRenderer,
          cellClassRules: {
            [EDITABLE_CELL_YELLOW_CLASS]: ({ value }: { value: number }) => value > 100,
          },
          cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        },
        mergePinnedRowValueFormatter({
          field: 'budgetedCost',
          headerName: this.translations.get('lbl_budgeted_cost_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        mergePinnedRowValueFormatter({
          field: 'invoicesToDate',
          headerName: this.translations.get('lbl_invoices_to_date_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        {
          field: 'invoicesToDatePct',
          headerName: this.translations.get('lbl_invoices_to_date_pct'),
          width: 100,
          ...buildNumberColumn(NumberTypes.Percents_2),
          cellRenderer: likeEditableCellRenderer,
          cellClassRules: {
            [EDITABLE_CELL_YELLOW_CLASS]: ({ value }: { value: number }) => value > 100,
          },
          cellRendererSelector: cellRendererSelectorForPinnedRow(noneCellRenderer),
        },
        mergePinnedRowValueFormatter({
          field: 'budgetedProfit',
          headerName: this.translations.get('lbl_budget_profit_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        mergePinnedRowValueFormatter({
          field: 'underruns',
          headerName: this.translations.get('lbl_underruns_calc_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        mergePinnedRowValueFormatter({
          field: 'overruns',
          headerName: this.translations.get('lbl_overruns_calc_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
        mergePinnedRowValueFormatter({
          field: 'overrunsAdjust',
          headerName: this.translations.get('lbl_overruns_adj_dollar'),
          width: 115,
          ...buildEditableNumberColumn(NumberTypes.SignedPrice, model.getCellAccessor('overrunsAdjust')),
          cellClassRules: {
            [CELL_TEXT_RIGHT_CLASS]: ({ node }: any) => node.isRowPinned(),
          },
          cellRendererSelector: cellRendererSelectorForPinnedRow(identityCellRenderer),
        }, editableValueFormatterDecorator(defaultCurrencyValueFormatterDecorator(
          typedValueFormatter(NumberTypes.SignedPrice)))),
        mergePinnedRowValueFormatter({
          field: 'wip',
          headerName: this.translations.get('lbl_wip_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
          hide: IS_REGION_AU,
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price))),
      ].concat(IS_REGION_AU
        ? [mergePinnedRowValueFormatter({
          field: 'wipAasb111',
          headerName: this.translations.get('lbl_wip_aasb_dollar'),
          width: 115,
          ...buildNumberColumn(NumberTypes.Price),
        }, defaultCurrencyValueFormatterDecorator(typedValueFormatter(NumberTypes.Price)))]
        : []),
    });
  }
}
